import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";
import { format } from "date-fns-tz";
import { parse } from "date-fns";

// Convert fiscal period number (YYYYMMDD) to readable date label
function formatFiscalPeriod(fiscalPeriod) {
  try {
    const str = String(fiscalPeriod);
    const date = parse(str, "yyyyMMdd", new Date());
    return format(date, "d MMMM yyyy", { timeZone: "Pacific/Auckland" });
  } catch {
    return String(fiscalPeriod);
  }
}

// FinanceService: loads file types and downloads finance report as xlsx
const FinanceService = (function () {
  let fileTypes = [];
  let isLoading = false;
  let loadingPromise = null;
  let activeLoadToken = null;

  let fiscalPeriods = [];
  let isFiscalPeriodsLoading = false;

  function getData() {
    return [...fileTypes];
  }

  async function loadData(forceRefresh = false) {
    if (isLoading && !forceRefresh) {
      return loadingPromise || Promise.resolve([...fileTypes]);
    }

    if (fileTypes.length > 0 && !forceRefresh) {
      Eventbus.emit(events.FILETYPES_LOADED, [...fileTypes]);
      return Promise.resolve([...fileTypes]);
    }

    isLoading = true;
    const token = {};
    activeLoadToken = token;

    loadingPromise = (async () => {
      try {
        const result = await api.getFileTypes();

        // If the service has been cleared or another load started since this
        // call began, ignore the result.
        if (activeLoadToken !== token) {
          return [];
        }

        if (result && result.code === "ERR_CANCELED") {
          return [...fileTypes];
        }

        fileTypes = Array.isArray(result) ? result.filter((item) => !!item.expectedBankAccNo).map((item) => ({ ...item, typeName: `${item.typeName} (${item.expectedBankAccNo})` })) : [];
        Eventbus.emit(events.FILETYPES_LOADED, [...fileTypes]);
        return [...fileTypes];
      } catch (error) {
        // Only log if this load is still the active one
        if (activeLoadToken === token) {
          logger.error("Error fetching file types:", error);
        }
        return [];
      } finally {
        // Only clear the loading state if this is still the active load
        if (activeLoadToken === token) {
          isLoading = false;
          loadingPromise = null;
          activeLoadToken = null;
        }
      }
    })();

    return loadingPromise;
  }

  function getLoadingState() {
    return isLoading;
  }

  function clear() {
    fileTypes = [];
    isLoading = false;
    loadingPromise = null;
    fiscalPeriods = [];
    isFiscalPeriodsLoading = false;
  }

  function getFiscalPeriodsData() {
    return [...fiscalPeriods];
  }

  function getFiscalPeriodsLoadingState() {
    return isFiscalPeriodsLoading;
  }

  async function loadFiscalPeriods() {
    if (isFiscalPeriodsLoading) return;
    isFiscalPeriodsLoading = true;
    try {
      await api.getFiscalPeriods();
    } catch (error) {
      logger.error("Error requesting fiscal periods:", error);
    } finally {
      isFiscalPeriodsLoading = false;
    }
  }

  // Normalize incoming items (PascalCase from API to camelCase with label)
  function normalizeFiscalPeriod(item) {
    const fiscalPeriod = item.FiscalPeriod ?? item.fiscalPeriod;
    const periodStatus = item.PeriodStatus ?? item.periodStatus;
    return {
      fiscalPeriod,
      periodStatus,
      label: formatFiscalPeriod(fiscalPeriod),
    };
  }

  // Merge incoming fiscal periods, deduplicate by fiscalPeriod value
  function mergeFiscalPeriods(incoming) {
    const incomingArray = (Array.isArray(incoming) ? incoming : [incoming]).map(normalizeFiscalPeriod);
    const merged = [...fiscalPeriods, ...incomingArray];
    const seen = new Set();
    fiscalPeriods = merged.filter((item) => {
      const key = item.fiscalPeriod;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    Eventbus.emit(events.FISCAL_PERIODS_LOADED, [...fiscalPeriods]);
  }

  // Listen for SignalR fiscal periods updates
  Eventbus.on(events.SIGNALR.FISCAL_PERIODS_UPDATED, (data) => {
    mergeFiscalPeriods(data);
  });

  async function downloadFinanceReport(dateRange, fileType) {
    try {
      if (!dateRange || !dateRange.from || !dateRange.to) {
        throw new Error("date range is required");
      }
      if (!fileType) {
        throw new Error("file type is required");
      }

      const fileTypeIds = Array.isArray(fileType) ? fileType.map((ft) => ft.fileTypeId) : [fileType.fileTypeId];
      if (fileTypeIds.length === 0) {
        throw new Error("at least one file type must be selected");
      }

      const { from, to } = dateRange;
      const DateFrom = typeof from === "string" ? from : format(from, "yyyy-MM-dd", { timeZone: "Pacific/Auckland" });
      const DateTo = typeof to === "string" ? to : format(to, "yyyy-MM-dd", { timeZone: "Pacific/Auckland" });

      const blob = await api.getFinanceReport(DateFrom, DateTo, fileTypeIds);
      if (!blob) return;

      // derive filename from selection
      const types = fileTypes.map(ft => ft.code);
      const filename = `${types?.join('_')}_${DateFrom}_${DateTo}.xlsx`;

      // trigger download
      const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      logger.debug("Finance report downloaded successfully:", filename);
    } catch (error) {
      logger.error("Error downloading finance report:", error);
      throw error;
    }
  }

  return {
    getData,
    loadData,
    clear,
    getLoadingState,
    downloadFinanceReport,
    getFiscalPeriodsData,
    getFiscalPeriodsLoadingState,
    loadFiscalPeriods,
    formatFiscalPeriod,
  };
})();

export default FinanceService;
